import React, { useState, useRef } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { Icons } from '../Icons';
import Logo from '../Logo';
import { STANDARD_LOGO_CONFIG } from '../Header';
import { useTranslation, LanguageDropdown } from 'utils/i18n';
import { isPlatformPublicLightModeOnly } from 'utils/platformPublicLightModeOnly';
import { useAuthState } from 'hooks/useAuthState';
import { useUserType, VIEW_MODES } from 'utils/userTypeContext';
import { authMethods } from 'utils/msalConfig';
import sharedTranslations from '../shared/translations';
import './MobileMoreMenu.css';

const MobileMoreMenu = ({ variant = 'customer', onLinkClick, onSignIn }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t: tCommon, language } = useTranslation();
  const { isAuthenticated, user } = useAuthState();
  const { viewMode } = useUserType();
  const msal = useMsal();
  const instance = msal?.instance ?? null;
  const [showAmdgTooltip, setShowAmdgTooltip] = useState(false);
  const [profileOpen, setProfileOpen] = useState(false);
  const profileRef = useRef(null);
  const currentYear = new Date().getFullYear();

  const getLocalizedPath = (path) => {
    const currentPath = location.pathname;
    const currentLangMatch = currentPath.match(/^\/([a-z]{2})\//);
    if (currentLangMatch) {
      return `/${language}${path}`;
    }
    return path;
  };

  const handleSignIn = () => {
    if (onSignIn && typeof onSignIn === 'function') {
      onSignIn();
      return;
    }
    if (instance) {
      authMethods.login(instance).catch(() => {});
    }
  };

  const handleLogout = () => {
    setProfileOpen(false);
    if (onLinkClick) onLinkClick();
    navigate('/auth/reset?reason=user_logout');
  };
  
  // Get shared component translations
  const getSharedTranslation = (key, defaultValue = '') => {
    const translations = sharedTranslations[language] || sharedTranslations.en;
    const value = key.split('.').reduce((obj, k) => obj?.[k], translations);
    if (value !== undefined && value !== null && value !== '') {
      return value;
    }
    const commonValue = tCommon(key);
    if (commonValue !== key && commonValue !== undefined) {
      return commonValue;
    }
    return defaultValue !== '' ? defaultValue : key;
  };

  const t = (key, defaultValue = '') => {
    const sharedValue = getSharedTranslation(key, '');
    if (sharedValue !== key && sharedValue !== '') {
      return sharedValue;
    }
    return tCommon(key, defaultValue);
  };

  const handleLinkClick = (path) => {
    if (onLinkClick) {
      onLinkClick();
    }
    if (path) {
      navigate(path);
    }
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleAmdgClick = () => {
    setShowAmdgTooltip(!showAmdgTooltip);
  };

  const handleAmdgBlur = () => {
    setTimeout(() => setShowAmdgTooltip(false), 150);
  };

  return (
    <div className="mobile-more-menu">
      {/* Account: Sign in (public) or Profile dropdown (auth, logged in) */}
      <div className="mobile-more-section">
        <h3 className="mobile-more-section-title">{getSharedTranslation('mobileMore.account', 'Account')}</h3>
        {onSignIn ? (
          <button type="button" className="mobile-more-signin-btn" onClick={handleSignIn}>
            <Icons.Login size={22} />
            <span>{t('header.signIn')}</span>
          </button>
        ) : isAuthenticated && user ? (
          <div className="mobile-more-profile-block" ref={profileRef}>
            <button
              type="button"
              className={`mobile-more-profile-trigger ${profileOpen ? 'open' : ''}`}
              onClick={() => setProfileOpen(!profileOpen)}
              aria-expanded={profileOpen}
            >
              <span className="mobile-more-profile-avatar">{user.initials || 'U'}</span>
              <div className="mobile-more-profile-info">
                <span className="mobile-more-profile-name">{user.name || user.email || 'User'}</span>
                <span className="mobile-more-profile-email">{user.email}</span>
              </div>
              <Icons.ChevronDown size={20} className="mobile-more-profile-chevron" />
            </button>
            {profileOpen && (
              <div className="mobile-more-profile-dropdown">
                <Link to={getLocalizedPath('/profile')} className="mobile-more-profile-item" onClick={() => { handleLinkClick('/profile'); setProfileOpen(false); }}>
                  <Icons.Profile size={20} />
                  <span>{t('header.profile')}</span>
                </Link>
                <Link to={getLocalizedPath('/app-users')} className="mobile-more-profile-item" onClick={() => { handleLinkClick('/app-users'); setProfileOpen(false); }}>
                  <Icons.Users size={20} />
                  <span>{t('header.userMenu.applicationUsers')}</span>
                </Link>
                <Link to={getLocalizedPath('/profile?tab=notifications')} className="mobile-more-profile-item" onClick={() => { handleLinkClick('/profile?tab=notifications'); setProfileOpen(false); }}>
                  <Icons.Bell size={20} />
                  <span>{t('header.userMenu.notificationPreferences')}</span>
                </Link>
                {viewMode === VIEW_MODES.NORMAL && (
                  <Link to={getLocalizedPath('/payment-profile')} className="mobile-more-profile-item" onClick={() => { handleLinkClick('/payment-profile'); setProfileOpen(false); }}>
                    <Icons.Money size={20} />
                    <span>{t('header.userMenu.payments', 'Payments')}</span>
                  </Link>
                )}
                <div className="mobile-more-profile-divider" />
                <button type="button" className="mobile-more-profile-item mobile-more-profile-logout" onClick={handleLogout}>
                  <Icons.Logout size={20} />
                  <span>{t('header.signOut')}</span>
                </button>
              </div>
            )}
          </div>
        ) : !onSignIn && instance ? (
          <button type="button" className="mobile-more-signin-btn" onClick={handleSignIn}>
            <Icons.Login size={22} />
            <span>{t('header.signIn')}</span>
          </button>
        ) : null}
      </div>

      {/* Language & Theme - card style to match mobile */}
      <div className="mobile-more-section">
        <h3 className="mobile-more-section-title">{getSharedTranslation('mobileMore.preferences', 'Preferences')}</h3>
        <div className="mobile-more-preferences-cards">
          <div className="mobile-more-pref-card">
            <span className="mobile-more-pref-card-label">{getSharedTranslation('mobileMore.language', 'Language')}</span>
            <div className="mobile-more-language-wrap">
              <LanguageDropdown className="mobile-more-language-dropdown" />
            </div>
          </div>
          {!isPlatformPublicLightModeOnly() && (
          <div className="mobile-more-pref-card">
            <span className="mobile-more-pref-card-label">{getSharedTranslation('mobileMore.theme', 'Theme')}</span>
            <div className="mobile-more-theme-switch">
              <button type="button" className="mobile-more-theme-btn active">
                <Icons.Sun size={22} />
                <span>{getSharedTranslation('header.lightMode', 'Light mode')}</span>
              </button>
            </div>
          </div>
          )}
        </div>
      </div>

      {/* Quick Actions Section - Only for logged-in users */}
      {variant !== 'public' && (
        <div className="mobile-more-section">
          <h3 className="mobile-more-section-title">{getSharedTranslation('mobileMore.quickActions', 'Quick Actions')}</h3>
          <div className="mobile-more-actions">
            {variant === 'customer' && (
              <button
                className="mobile-more-action-btn"
                onClick={() => handleLinkClick('/payment-profile')}
              >
                <Icons.Money size={20} />
                <span>{getSharedTranslation('mobileMore.paymentProfile', 'Payment Profile')}</span>
              </button>
            )}
            <button
              className="mobile-more-action-btn"
              onClick={() => handleLinkClick('/marketplace')}
            >
              <Icons.Store size={20} />
              <span>{getSharedTranslation('mobileMore.marketplace', 'Marketplace')}</span>
            </button>
          </div>
        </div>
      )}

      {/* Product Links */}
      <div className="mobile-more-section">
        <h3 className="mobile-more-section-title">{t('footer.product.title')}</h3>
        <ul className="mobile-more-links">
          <li>
            <Link to="/about" className="mobile-more-link" onClick={() => handleLinkClick('/about')}>
              {t('footer.product.about')}
            </Link>
          </li>
          <li>
            <Link to="/roadmap" className="mobile-more-link" onClick={() => handleLinkClick('/roadmap')}>
              {t('footer.product.roadmap')}
            </Link>
          </li>
          <li>
            <Link to="/sla" className="mobile-more-link" onClick={() => handleLinkClick('/sla')}>
              {t('footer.product.sla')}
            </Link>
          </li>
          <li>
            <Link to="/marketplace" className="mobile-more-link" onClick={() => handleLinkClick('/marketplace')}>
              {t('footer.product.marketplace')}
            </Link>
          </li>
        </ul>
      </div>

      {/* Support Links */}
      <div className="mobile-more-section">
        <h3 className="mobile-more-section-title">{t('footer.support.title')}</h3>
        <ul className="mobile-more-links">
          <li>
            <Link to="/faq" className="mobile-more-link" onClick={() => handleLinkClick('/faq')}>{t('footer.support.faq')}</Link>
          </li>
          <li>
            <Link to="/docs/getting-started/quickstart" className="mobile-more-link" onClick={() => handleLinkClick('/docs/getting-started/quickstart')}>{t('footer.support.developerDocs', 'Developer docs')}</Link>
          </li>
          <li>
            <Link to="/bug-reports" className="mobile-more-link" onClick={() => handleLinkClick('/bug-reports')}>{t('footer.support.bugReports')}</Link>
          </li>
          <li>
            <Link to="/contact" className="mobile-more-link" onClick={() => handleLinkClick('/contact')}>{t('footer.support.contact')}</Link>
          </li>
          <li>
            <Link to="/help-center" className="mobile-more-link" onClick={() => handleLinkClick('/help-center')}>{t('footer.support.helpCenter')}</Link>
          </li>
          <li>
            <Link to="/community" className="mobile-more-link" onClick={() => handleLinkClick('/community')}>{t('footer.support.community')}</Link>
          </li>
          <li>
            <Link to="/become-partner" className="mobile-more-link" onClick={() => handleLinkClick('/become-partner')}>{t('footer.support.becomePartner')}</Link>
          </li>
          <li>
            <Link to="/feedback" className="mobile-more-link" onClick={() => handleLinkClick('/feedback')}>{t('footer.support.feedback')}</Link>
          </li>
        </ul>
      </div>

      {/* Legal Links */}
      <div className="mobile-more-section">
        <h3 className="mobile-more-section-title">{t('footer.legal.title')}</h3>
        <ul className="mobile-more-links">
          <li>
            <Link to="/terms" className="mobile-more-link" onClick={() => handleLinkClick('/terms')}>
              {t('footer.legal.terms')}
            </Link>
          </li>
          <li>
            <Link to="/privacy" className="mobile-more-link" onClick={() => handleLinkClick('/privacy')}>
              {t('footer.legal.privacy')}
            </Link>
          </li>
          <li>
            <Link to="/eula" className="mobile-more-link" onClick={() => handleLinkClick('/eula')}>
              {t('footer.legal.eula')}
            </Link>
          </li>
          <li>
            <Link to="/acceptable-use" className="mobile-more-link" onClick={() => handleLinkClick('/acceptable-use')}>
              {t('footer.legal.acceptableUse')}
            </Link>
          </li>
          <li>
            <Link to="/disclaimer" className="mobile-more-link" onClick={() => handleLinkClick('/disclaimer')}>
              {t('footer.legal.disclaimer')}
            </Link>
          </li>
          <li>
            <Link to="/impressum" className="mobile-more-link" onClick={() => handleLinkClick('/impressum')}>
              {t('footer.legal.imprint')}
            </Link>
          </li>
          <li>
            <Link to="/cookie-settings" className="mobile-more-link" onClick={() => handleLinkClick('/cookie-settings')}>
              {t('footer.legal.cookieSettings')}
            </Link>
          </li>
          <li>
            <Link to="/gdpr" className="mobile-more-link" onClick={() => handleLinkClick('/gdpr')}>
              {t('footer.legal.gdpr')}
            </Link>
          </li>
          <li>
            <Link to="/dpa" className="mobile-more-link" onClick={() => handleLinkClick('/dpa')}>
              {t('footer.legal.dpa')}
            </Link>
          </li>
        </ul>
      </div>

      {/* Company Links */}
      <div className="mobile-more-section">
        <h3 className="mobile-more-section-title">{t('footer.company.title')}</h3>
        <ul className="mobile-more-links">
          <li>
            <Link to="/architecture" className="mobile-more-link" onClick={() => handleLinkClick('/architecture')}>
              {t('footer.company.architecture')}
            </Link>
          </li>
          <li>
            <Link to="/strategy" className="mobile-more-link" onClick={() => handleLinkClick('/strategy')}>
              {t('footer.company.ourStrategy')}
            </Link>
          </li>
        </ul>
      </div>

      {/* Company Info & Contact */}
      <div className="mobile-more-section">
        <div className="mobile-more-company-info">
          <div className="mobile-more-logo">
            <Logo {...STANDARD_LOGO_CONFIG} />
          </div>
          <p className="mobile-more-description">
            {t('footer.description')}
          </p>
          <div className="mobile-more-contact">
            <a href="mailto:info@b-esper.com" className="mobile-more-contact-item">
              <Icons.Mail size={16} />
              <span>info@b-esper.com</span>
            </a>
            <a href="tel:+491716724126" className="mobile-more-contact-item">
              <Icons.Phone size={16} />
              <span>+49 171 67 24 126</span>
            </a>
          </div>
        </div>
      </div>

      {/* Social Links */}
      <div className="mobile-more-section">
        <div className="mobile-more-social">
          <a
            href="https://x.com/Besper_official"
            className="mobile-more-social-link"
            aria-label={t('footer.bottom.socialMedia.twitter', 'Follow us on Twitter')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icons.Twitter size={20} />
          </a>
          <a
            href="https://www.linkedin.com/company/106351579"
            className="mobile-more-social-link"
            aria-label={t('footer.bottom.socialMedia.linkedin', 'Follow us on LinkedIn')}
            target="_blank"
            rel="noopener noreferrer"
          >
            <Icons.LinkedIn size={20} />
          </a>
        </div>
      </div>

      {/* Footer Bottom */}
      <div className="mobile-more-footer">
        <p className="mobile-more-copyright">
          {t('footer.bottom.copyright', `© ${currentYear} B-esper GmbH. All rights reserved.`).replace('{year}', currentYear)}
        </p>
        <div className="mobile-more-meta">
          <span>{t('footer.bottom.madeInGermany', 'Made in Germany')}</span>
          <span>•</span>
          <span>{t('footer.bottom.gdprCompliant', 'GDPR Compliant')}</span>
          <span>•</span>
          <span>{t('footer.bottom.euHosting', 'EU Hosting')}</span>
          <span>•</span>
          <div className="mobile-more-amdg-container">
            <button
              className="mobile-more-amdg"
              onClick={handleAmdgClick}
              onBlur={handleAmdgBlur}
              title={t('footer.bottom.amdg.tooltip.title', 'Click to learn more')}
              aria-label={t('footer.bottom.amdg.tooltip.ariaLabel', 'A.M.D.G. - Click to learn more')}
            >
              A.M.D.G.
            </button>
            {showAmdgTooltip && (
              <div className="mobile-more-amdg-tooltip">
                <div className="mobile-more-amdg-tooltip-content">
                  <strong>{t('footer.bottom.amdg.tooltip.latin', 'Ad Majorem Dei Gloriam')}</strong>
                  <p>{t('footer.bottom.amdg.tooltip.translation', 'For the greater glory of God')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileMoreMenu;


